
import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { Headset } from "lucide-react"

import { cn } from "@/lib/utils"
import { GlowEffect } from "./glow-effect"

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & {
    variant?: "default" | "brand" | "glow" | "simple";
  }
>(({ className, variant = "default", ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      {
        "border-2 border-gold": variant === "brand",
        "border border-gold/30": variant === "glow",
        "bg-gold": variant === "simple",
      },
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
))
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
    glowEffect?: boolean;
    icon?: boolean;
  }
>(({ className, glowEffect = false, icon = false, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "relative flex h-full w-full items-center justify-center rounded-full",
      {
        "bg-muted": !glowEffect && className?.includes("bg-") === false,
        "bg-luxury-900 text-gold": glowEffect,
      },
      className
    )}
    {...props}
  >
    {glowEffect && (
      <GlowEffect
        className="opacity-50 z-0"
        colors={["#c8a87d", "#e6d5b8", "#a68756"]}
        mode="breathe"
        blur="soft"
        scale={1.2}
      />
    )}
    {icon ? <Headset size={18} className="text-gold" /> : props.children}
  </AvatarPrimitive.Fallback>
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
